import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, FileUp, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addWord,
  addWordToStudySet,
  createStudySet,
  getStudySets,
} from "@/hooks/useFirestore";
import { extractWordsFromText, translateText } from "@/hooks/useDirectApi";
import { enrichGrammarInBackground } from "@/lib/grammarEnrich";
import { parseImportFile, parseTextContent, ImportedPair } from "@/lib/importParse";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

type ImportStep = "pick" | "parsing" | "preview" | "importing" | "done";

interface PreviewWord extends ImportedPair {
  selected: boolean;
}

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCEPTED = ".txt,.csv,.tsv,.xlsx,.xls,.pdf";

/**
 * Import words from an external file (text list, spreadsheet, or PDF).
 * Clean files parse client-side; messy ones go through LLM extraction.
 * Words save instantly (fast import); missing translations are auto-filled
 * and grammar enrichment runs in the background.
 */
export function ImportDialog({ open, onOpenChange }: ImportDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<ImportStep>("pick");
  const [pastedText, setPastedText] = useState("");
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [previewWords, setPreviewWords] = useState<PreviewWord[]>([]);
  const [destination, setDestination] = useState<string>("");
  const [newSetName, setNewSetName] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0, added: 0, failed: 0 });

  const { data: studySets } = useQuery({
    queryKey: ["studySets", user?.uid],
    queryFn: () => getStudySets(user!.uid),
    enabled: !!user && open,
  });

  const reset = () => {
    setStep("pick");
    setPastedText("");
    setFileName("");
    setParseError(null);
    setPreviewWords([]);
    setDestination("");
    setNewSetName("");
    setProgress({ current: 0, total: 0, added: 0, failed: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (nextOpen: boolean) => {
    // Don't allow closing mid-import
    if (!nextOpen && step === "importing") return;
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const showPreview = (pairs: ImportedPair[]) => {
    if (pairs.length === 0) {
      setParseError(
        "No Hebrew words found. Make sure the content contains Hebrew vocabulary."
      );
      setStep("pick");
      return;
    }
    setPreviewWords(pairs.map((p) => ({ ...p, selected: true })));
    setStep("preview");
  };

  const handleParseError = (err: unknown) => {
    console.error("Import parse failed:", err);
    setParseError(
      err instanceof Error ? err.message : "Couldn't read this content. Try a different format."
    );
    setStep("pick");
  };

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setParseError(null);
    setStep("parsing");

    try {
      const result = await parseImportFile(file);
      const pairs =
        result.kind === "pairs"
          ? result.pairs
          : await extractWordsFromText(result.text); // Messy content — LLM extraction
      showPreview(pairs);
    } catch (err) {
      handleParseError(err);
    }
  };

  const handlePastedText = async () => {
    const text = pastedText.trim();
    if (!text) return;
    setFileName("pasted text");
    setParseError(null);
    setStep("parsing");

    try {
      const result = parseTextContent(text);
      const pairs =
        result.kind === "pairs"
          ? result.pairs
          : await extractWordsFromText(result.text);
      showPreview(pairs);
    } catch (err) {
      handleParseError(err);
    }
  };

  const toggleWord = (index: number) => {
    setPreviewWords((prev) =>
      prev.map((w, i) => (i === index ? { ...w, selected: !w.selected } : w))
    );
  };

  const selectedCount = previewWords.filter((w) => w.selected).length;

  const runImport = async () => {
    if (!user) return;
    const toImport = previewWords.filter((w) => w.selected);
    setProgress({ current: 0, total: toImport.length, added: 0, failed: 0 });
    setStep("importing");

    // Resolve destination study set (existing id, or create new)
    let targetSetId: string | null = null;
    try {
      if (destination === "__new__" && newSetName.trim()) {
        const newSet = await createStudySet(user.uid, newSetName.trim(), "Imported words");
        targetSetId = newSet.id;
      } else if (destination && destination !== "__new__") {
        targetSetId = destination;
      }
    } catch (err) {
      console.error("Failed to create study set:", err);
    }

    const added: { wordId: string; hebrew: string }[] = [];
    let failed = 0;

    for (let i = 0; i < toImport.length; i++) {
      const w = toImport[i];
      try {
        // Fill in missing translation via Google Translate
        let translation = w.translation?.trim();
        if (!translation) {
          const result = await translateText(w.hebrew, "he", "en");
          translation = result.translatedText;
        }

        const wordId = await addWord(user.uid, w.hebrew, translation, "en", []);
        added.push({ wordId, hebrew: w.hebrew });

        if (targetSetId) {
          await addWordToStudySet(user.uid, targetSetId, wordId);
        }
      } catch (err) {
        console.error("Failed to import word:", w.hebrew, err);
        failed++;
      }
      setProgress({
        current: i + 1,
        total: toImport.length,
        added: added.length,
        failed,
      });
    }

    queryClient.invalidateQueries({ queryKey: ["words"] });
    queryClient.invalidateQueries({ queryKey: ["categories"] });
    queryClient.invalidateQueries({ queryKey: ["studySets"] });
    queryClient.invalidateQueries({ queryKey: ["studySetWords"] });

    // Background grammar enrichment — fire and forget
    if (added.length > 0) {
      enrichGrammarInBackground(user.uid, added, queryClient).catch((err) =>
        console.error("[import] Background grammar enrichment failed:", err)
      );
    }

    setStep("done");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Words</DialogTitle>
          <DialogDescription>
            Upload a word list, spreadsheet, or PDF. Hebrew words are detected
            automatically; missing translations are filled in for you.
          </DialogDescription>
        </DialogHeader>

        {/* Step: pick file */}
        {step === "pick" && (
          <div className="space-y-4">
            <button
              className="w-full border-2 border-dashed rounded-lg p-8 text-center hover:border-primary hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUp className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="font-medium">Choose a file</p>
              <p className="text-sm text-muted-foreground mt-1">
                .txt, .csv, .xlsx, or .pdf
              </p>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
            {/* Or paste a list */}
            <div className="flex items-center gap-3">
              <div className="h-px bg-border flex-1" />
              <span className="text-xs text-muted-foreground">or paste a list</span>
              <div className="h-px bg-border flex-1" />
            </div>
            <textarea
              className="w-full border rounded-md p-2 text-sm bg-background min-h-24 resize-y"
              placeholder={"שלום - hello\nמשפחה - family\n…or any text with Hebrew words in it"}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
            />
            <Button
              className="w-full"
              onClick={handlePastedText}
              disabled={!pastedText.trim()}
            >
              Continue
            </Button>

            {parseError && (
              <p className="text-sm text-destructive text-center">{parseError}</p>
            )}
            <p className="text-xs text-muted-foreground text-center">
              Works with lists like "שלום - hello", two-column spreadsheets, or
              vocabulary PDFs.
            </p>
          </div>
        )}

        {/* Step: parsing */}
        {step === "parsing" && (
          <div className="py-8 text-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Reading {fileName}…</p>
          </div>
        )}

        {/* Step: preview */}
        {step === "preview" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Found {previewWords.length} word{previewWords.length === 1 ? "" : "s"} in{" "}
              <span className="font-medium">{fileName}</span>. Uncheck any you don't want.
            </p>

            <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
              {previewWords.map((w, i) => (
                <button
                  key={i}
                  className="w-full flex items-center gap-3 p-2 text-left hover:bg-muted/50"
                  onClick={() => toggleWord(i)}
                >
                  <div
                    className={cn(
                      "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                      w.selected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-muted-foreground"
                    )}
                  >
                    {w.selected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="font-medium hebrew-text" dir="rtl">
                    {w.hebrew}
                  </span>
                  <span className="text-sm text-muted-foreground truncate">
                    {w.translation || (
                      <em className="text-xs">will auto-translate</em>
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Add to</label>
              <select
                className="w-full border rounded-md p-2 text-sm bg-background"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                <option value="">Word bank only</option>
                <option value="__new__">＋ New study set…</option>
                {studySets?.map((set) => (
                  <option key={set.id} value={set.id}>
                    Study set: {set.name}
                  </option>
                ))}
              </select>
              {destination === "__new__" && (
                <input
                  className="w-full border rounded-md p-2 text-sm bg-background"
                  placeholder="New study set name"
                  value={newSetName}
                  onChange={(e) => setNewSetName(e.target.value)}
                />
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={reset}>
                <X className="mr-2 h-4 w-4" />
                Start Over
              </Button>
              <Button
                onClick={runImport}
                disabled={
                  selectedCount === 0 ||
                  (destination === "__new__" && !newSetName.trim())
                }
              >
                <Upload className="mr-2 h-4 w-4" />
                Import {selectedCount} Word{selectedCount === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: importing / done */}
        {(step === "importing" || step === "done") && (
          <div className="py-6 text-center space-y-4">
            {step === "done" ? (
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="h-6 w-6 text-green-600" />
              </div>
            ) : (
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            )}

            {/* Progress bar */}
            <div className="max-w-xs mx-auto space-y-1">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{
                    width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {progress.current} / {progress.total}
              </p>
            </div>

            <p className="text-sm">
              {step === "done" ? (
                <>
                  Imported <span className="font-semibold">{progress.added}</span> word
                  {progress.added === 1 ? "" : "s"}
                  {progress.failed > 0 && (
                    <span className="text-destructive"> ({progress.failed} failed)</span>
                  )}
                  . Grammar info is being added in the background.
                </>
              ) : (
                "Saving words and translating where needed…"
              )}
            </p>

            {step === "done" && (
              <Button onClick={() => handleClose(false)}>Done</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
